import type { IEvaultApi } from './use-evault-api';
import type {
	IDocumentFilters,
	IDriveView,
	IPaginated,
	IVault,
	IVaultDocument,
	IVaultEntitlement,
	IVaultFilters,
	IVaultStatus,
} from './types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EVAULT_API, toQuery } from './use-evault-api';

/**
 * The server's own default (`vaults/index.ts` DEFAULT_LIMIT). Named rather than
 * inlined so a reviewer can drop it to 3 and watch the pager work against a
 * nine-vault seed without touching anything else.
 */
export const DRIVE_PAGE_LIMIT = 24;

/** Long enough that a typed word is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 300;

const VIEW_KEY = 'simplici-evault-view';

export const DEFAULT_VAULT_FILTERS: IVaultFilters = {
	search: '',
	modified: null,
	source: null,
	sort: 'name',
	order: 'asc',
};

export const DEFAULT_DOCUMENT_FILTERS: IDocumentFilters = {
	search: '',
	type: null,
	modified: null,
	sort: 'depositedAt',
	order: 'desc',
	/* The drive shows what is IN the vault unless asked otherwise — the server's own
	   default, restated here so the query is explicit rather than implied by omission. */
	state: 'vaulted',
};

export interface IListState<T> {
	isLoaded: boolean;
	isLoading: boolean;
	/** The server's own sentence, or '' when the last load succeeded. */
	error: string;
	items: T[];
	total: number;
}

const EMPTY_LIST = {
	isLoaded: false,
	isLoading: false,
	error: '',
	items: [],
	total: 0,
};

/**
 * `null` is UNKNOWN, not "no".
 *
 * The probe can fail for reasons that have nothing to do with billing (esign.api
 * down, token not yet minted), and treating that as unentitled would paywall a
 * paying customer over a network blip. The backend is the real gate — it 403s
 * `EVAULT_NOT_ENTITLED` on create and transfer regardless of what this says — so
 * unknown is rendered as permissive and the refusal, if it comes, opens the
 * upsell where it happened.
 */
export interface IEntitlementState {
	isLoaded: boolean;
	entitled: boolean | null;
	plan: string;
}

/**
 * The entitlement probe, on its own so both surfaces can ask.
 *
 * The drive needs it for the band and the Create button; the vault page needs it
 * for the Transfer control in its bulk bar. Neither is the owner of the answer,
 * so it lives here rather than inside the drive's hook, where the vault page
 * would have had to instantiate the whole list machinery to read one boolean.
 */
export const useEntitlement = (
	api: IEvaultApi
): IEntitlementState & { refetch: () => Promise<void> } => {
	const { get, vaultToken } = api;
	const [entitlement, setEntitlement] = useState<IEntitlementState>({
		isLoaded: false,
		entitled: null,
		plan: '',
	});

	const fetchEntitlement = useCallback(async () => {
		if (!vaultToken) return;
		const result = await get<IVaultEntitlement>(EVAULT_API.VAULT_ENTITLEMENT);
		setEntitlement({
			isLoaded: true,
			entitled: result.kind === 'ok' ? !!result.data?.entitled : null,
			plan: result.kind === 'ok' ? result.data?.plan ?? '' : '',
		});
	}, [get, vaultToken]);

	/*
	 * ON THE TOKEN'S PRESENCE, NOT ITS VALUE.
	 *
	 * `fetchEntitlement` is rebuilt whenever the bearer changes, so depending on
	 * it means re-probing entitlement every time `useVaultToken` re-mints — which
	 * it does silently every ~14 minutes, and TWICE on mount under React's
	 * StrictMode double-invoke. That produced the duplicate `GET
	 * /vault-entitlement` (and, through the same route below, the duplicate `GET
	 * /vaults`) visible in the network tab on every dev load.
	 *
	 * Entitlement is a property of the workspace's subscription, not of the
	 * credential used to ask, so a fresh token is not new information. The one
	 * probe is enough: the backend is the real gate and 403s `EVAULT_NOT_ENTITLED`
	 * on create and transfer regardless of what this answered.
	 */
	const hasToken = !!vaultToken;
	useEffect(() => {
		void fetchEntitlement();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasToken]);

	return { ...entitlement, refetch: fetchEntitlement };
};

/** Read once, tolerating a browser that refuses storage entirely. */
const readView = (): IDriveView => {
	try {
		return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'list';
	} catch {
		return 'list';
	}
};

export const useDriveView = (): [IDriveView, (next: IDriveView) => void] => {
	const [view, setView] = useState<IDriveView>(readView);

	const choose = useCallback((next: IDriveView) => {
		setView(next);
		try {
			localStorage.setItem(VIEW_KEY, next);
		} catch {
			/* private mode: the choice still holds for this visit */
		}
	}, []);

	return [view, choose];
};

/* -------------------------------------------------------------------------- */
/*                                   Drive                                    */
/* -------------------------------------------------------------------------- */

/**
 * Vault list + entitlement + selection for `/evault`.
 *
 * Deck state is local (useState), not Recoil — the same call sessions, eSign and
 * funds made. Nothing outside this route reads it, and a page-scoped atom that
 * survives navigation is how a stale selection ends up driving a document query
 * on a screen the user has already left.
 */
export const useEvaultDeck = (api: IEvaultApi) => {
	const { get, vaultToken } = api;

	const [tab, setTab] = useState<IVaultStatus>('active');
	const [typedSearch, setTypedSearch] = useState('');
	const [filters, setFilters] = useState<IVaultFilters>(DEFAULT_VAULT_FILTERS);
	const [page, setPage] = useState(1);
	const [vaults, setVaults] = useState<IListState<IVault>>(EMPTY_LIST);
	const entitlement = useEntitlement(api);
	const [selectedVaultIds, setSelectedVaultIds] = useState<string[]>([]);

	/* A slow first request must not land on top of a fast second one. Every
	   fetch claims a ticket; only the newest ticket is allowed to write. */
	const ticket = useRef(0);

	const query = useMemo(
		() =>
			toQuery({
				search: filters.search,
				modified: filters.modified,
				source: filters.source,
				sort: filters.sort,
				order: filters.order,
				status: tab,
				page: String(page),
				limit: String(DRIVE_PAGE_LIMIT),
			}),
		[filters, tab, page]
	);

	const fetchVaults = useCallback(async () => {
		if (!vaultToken) return;
		const mine = (ticket.current += 1);
		setVaults(prev => ({ ...prev, isLoading: true, error: '' }));

		const result = await get<IPaginated<IVault>>(`${EVAULT_API.VAULTS}${query}`);
		if (mine !== ticket.current) return;

		if (result.kind !== 'ok') {
			/* Never the empty state. A failed load and an empty drive look
			   identical in a table and mean opposite things — the drive's own
			   comment upstream says so, and this is the rule it states. */
			setVaults(prev => ({
				...prev,
				isLoaded: true,
				isLoading: false,
				error: result.message,
			}));
			return;
		}

		setVaults({
			isLoaded: true,
			isLoading: false,
			error: '',
			items: result.data?.items ?? [],
			total: result.data?.total ?? 0,
		});
	}, [get, query, vaultToken]);

	/* Debounce the box, not the request: `typedSearch` renders every keystroke,
	   `filters.search` is what the server ever sees. */
	useEffect(() => {
		const timer = setTimeout(() => {
			setFilters(prev =>
				prev.search === typedSearch ? prev : { ...prev, search: typedSearch }
			);
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [typedSearch]);

	/* THE QUERY AND THE TOKEN'S PRESENCE, not `fetchVaults` itself — see the note
	   in `useEntitlement`. `fetchVaults` is a new function on every re-mint of
	   the bearer, and a silent token refresh is not a reason to re-read a page
	   the reader is already looking at. `refetchVaults` is still the explicit
	   way to reload it, and create/rename/archive all call it. */
	const hasToken = !!vaultToken;
	useEffect(() => {
		void fetchVaults();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasToken, query]);

	/* Switching tabs clears the selection and resets the page (§5c): an archived
	   vault's documents are not what was selected on the Active tab. */
	const chooseTab = useCallback((next: IVaultStatus) => {
		setTab(next);
		setPage(1);
		setSelectedVaultIds([]);
	}, []);

	const setFilter = useCallback(
		<IKey extends keyof IVaultFilters>(key: IKey, value: IVaultFilters[IKey]) => {
			setFilters(prev => ({ ...prev, [key]: value }));
			setPage(1);
		},
		[]
	);

	const toggleVault = useCallback((id: string) => {
		setSelectedVaultIds(prev =>
			prev.includes(id) ? prev.filter(one => one !== id) : [...prev, id]
		);
	}, []);

	const clearSelection = useCallback(() => setSelectedVaultIds([]), []);

	/**
	 * The header checkbox, over the rows CURRENTLY ON SCREEN.
	 *
	 * `ids` is what the caller can actually tick — the page's rows, minus any
	 * row whose own checkbox is disabled — so select-all never selects
	 * something a row-level click could not. Clearing to `[]` rather than
	 * subtracting is right because the selection is already page-scoped: it is
	 * dropped whenever the list changes underneath it.
	 */
	const toggleAllVaults = useCallback((ids: string[]) => {
		setSelectedVaultIds(prev =>
			ids.length > 0 && ids.every(id => prev.includes(id)) ? [] : ids
		);
	}, []);

	/**
	 * Discretionary filters only — never sort. Sort always has a value, so
	 * counting it would make the drive look permanently filtered and collapse
	 * the "no vaults yet" / "no results" distinction the two empty states rest
	 * on.
	 */
	const activeFilterCount = useMemo(
		() =>
			(filters.search.trim() ? 1 : 0) +
			(filters.modified ? 1 : 0) +
			(filters.source ? 1 : 0),
		[filters]
	);

	const pages = Math.max(1, Math.ceil(vaults.total / DRIVE_PAGE_LIMIT));

	return {
		tab,
		chooseTab,
		typedSearch,
		setTypedSearch,
		filters,
		setFilter,
		page,
		setPage,
		pages,
		vaults,
		refetchVaults: fetchVaults,
		entitlement,
		selectedVaultIds,
		toggleVault,
		toggleAllVaults,
		clearSelection,
		activeFilterCount,
	};
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

/**
 * The documents of a set of vaults.
 *
 * One hook for both callers, because there is one endpoint for both: the drive
 * passes the whole vault selection and gets the union, the vault page passes a
 * single id (decision D5). `vaultIds` is REQUIRED server-side — an empty value
 * is a 400, never an implicit fetch-everything — so an empty selection issues no
 * request at all and the panel renders its hint line instead.
 */
export const useVaultDocuments = (api: IEvaultApi, vaultIds: string[]) => {
	const { get, vaultToken } = api;

	const [typedSearch, setTypedSearch] = useState('');
	const [filters, setFilters] = useState<IDocumentFilters>(
		DEFAULT_DOCUMENT_FILTERS
	);
	const [page, setPage] = useState(1);
	const [documents, setDocuments] =
		useState<IListState<IVaultDocument>>(EMPTY_LIST);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const ticket = useRef(0);

	/* Joined here so the dependency is a string: a fresh array literal from the
	   parent every render would otherwise refetch forever. */
	const vaultKey = useMemo(() => vaultIds.join(','), [vaultIds]);

	const query = useMemo(
		() =>
			toQuery({
				vaultIds: vaultKey,
				state: filters.state,
				search: filters.search,
				type: filters.type,
				modified: filters.modified,
				sort: filters.sort,
				order: filters.order,
				page: String(page),
				limit: String(DRIVE_PAGE_LIMIT),
			}),
		[vaultKey, filters, page]
	);

	const fetchDocuments = useCallback(async () => {
		if (!vaultToken) return;
		if (!vaultKey) {
			setDocuments({ ...EMPTY_LIST, isLoaded: true });
			return;
		}
		const mine = (ticket.current += 1);
		setDocuments(prev => ({ ...prev, isLoading: true, error: '' }));

		const result = await get<IPaginated<IVaultDocument>>(
			`${EVAULT_API.VAULT_DOCUMENTS}${query}`
		);
		if (mine !== ticket.current) return;

		if (result.kind !== 'ok') {
			setDocuments(prev => ({
				...prev,
				isLoaded: true,
				isLoading: false,
				error: result.message,
			}));
			return;
		}

		setDocuments({
			isLoaded: true,
			isLoading: false,
			error: '',
			items: result.data?.items ?? [],
			total: result.data?.total ?? 0,
		});
	}, [get, query, vaultKey, vaultToken]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setFilters(prev =>
				prev.search === typedSearch ? prev : { ...prev, search: typedSearch }
			);
			setPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [typedSearch]);

	/* As above: the query and whether a bearer exists, never the bearer itself. */
	const hasToken = !!vaultToken;
	useEffect(() => {
		void fetchDocuments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasToken, query]);

	/* A document selected under one vault selection must not stay selected once
	   that vault is deselected — the bulk bar would then count a document the
	   drive is no longer showing, which is §2.10 in the other direction. */
	useEffect(() => {
		setSelectedIds([]);
		setPage(1);
	}, [vaultKey]);

	/**
	 * A selected row that the list no longer returns is not a selection.
	 *
	 * The case that made this visible: a committed transfer refetches this list,
	 * the transferred original leaves it, and the id stayed behind in
	 * `selectedIds` — so the bulk bar sat there reading "0 documents selected ·
	 * Transfer ownership (0)", visible because the ids were non-empty and empty
	 * because the documents behind them were gone. A search or filter that
	 * narrows the page does the same thing. The selection is what is on screen.
	 */
	useEffect(() => {
		if (!documents.isLoaded) return;
		const live = new Set(documents.items.map(doc => doc._id));
		setSelectedIds(prev => {
			const next = prev.filter(id => live.has(id));
			return next.length === prev.length ? prev : next;
		});
	}, [documents.items, documents.isLoaded]);

	const setFilter = useCallback(
		<IKey extends keyof IDocumentFilters>(
			key: IKey,
			value: IDocumentFilters[IKey]
		) => {
			setFilters(prev => ({ ...prev, [key]: value }));
			setPage(1);
		},
		[]
	);

	const toggleDocument = useCallback((id: string) => {
		setSelectedIds(prev =>
			prev.includes(id) ? prev.filter(one => one !== id) : [...prev, id]
		);
	}, []);

	const clearDocuments = useCallback(() => setSelectedIds([]), []);

	/** Select-all over the rows on screen — see `toggleAllVaults`. The caller
	 *  passes only the selectable ones, which here means "not a retained copy". */
	const toggleAllDocuments = useCallback((ids: string[]) => {
		setSelectedIds(prev =>
			ids.length > 0 && ids.every(id => prev.includes(id)) ? [] : ids
		);
	}, []);

	/* `state` is deliberately NOT counted. It is not a filter over the drive, it is WHICH
	   drive — and counting it would make an empty `Checked out` list read as "no
	   documents match these filters" when what it means is that nothing has ever been
	   checked out. */
	const activeFilterCount = useMemo(
		() => (filters.search.trim() ? 1 : 0) + (filters.type ? 1 : 0),
		[filters]
	);

	const pages = Math.max(1, Math.ceil(documents.total / DRIVE_PAGE_LIMIT));

	return {
		typedSearch,
		setTypedSearch,
		filters,
		setFilter,
		page,
		setPage,
		pages,
		documents,
		refetchDocuments: fetchDocuments,
		selectedIds,
		toggleDocument,
		toggleAllDocuments,
		clearDocuments,
		activeFilterCount,
	};
};
